// ═══════════════════════════════════════════════════════════════════
//  CrossSheetGhostsLayer — Faint dashed lines for cross-sheet refs.
// ═══════════════════════════════════════════════════════════════════
//
//  When the workflow has multiple sheets and a cross-sheet connector
//  touches a block on the active sheet, this layer draws a dashed
//  line from that block to the top edge of the minimap, pointing
//  toward the target sheet's pill in the SheetTabStrip above.
//
//  Pill positions aren't passed in directly — we approximate by
//  spacing pills evenly across `minimapWidth`. That matches the
//  SheetTabStrip render order and stays correct when pills wrap or
//  scroll because the live strip uses the same proportional spacing.
//
// ═══════════════════════════════════════════════════════════════════

import { useMemo } from 'react';
import type { CrossSheetConnector, WorkflowBlock, BlockDimensions } from '@/types/workflow';

export interface CrossSheetGhostsLayerProps {
  blocks: WorkflowBlock[];
  blockDimensions?: Record<string, BlockDimensions>;
  crossSheetConnectors: CrossSheetConnector[];
  activeSheetIndex: number;
  sheetCount: number;
  toScreenX: (x: number) => number;
  toScreenY: (y: number) => number;
  scale: number;
  width: number;
  height: number;
}

interface GhostLine {
  cscId: string;
  /** Block-side anchor (minimap-pixel). */
  bx: number;
  by: number;
  /** Strip-side anchor (top edge, x-position pointing at target pill). */
  px: number;
  py: number;
  /** Target sheet index (for the title tooltip). */
  toSheet: number;
}

export function CrossSheetGhostsLayer({
  blocks,
  blockDimensions,
  crossSheetConnectors,
  activeSheetIndex,
  sheetCount,
  toScreenX,
  toScreenY,
  scale,
  width,
  height,
}: CrossSheetGhostsLayerProps) {
  const lines = useMemo<GhostLine[]>(() => {
    if (sheetCount < 2 || crossSheetConnectors.length === 0) return [];
    const blockById = new Map(blocks.map((b) => [b.id, b]));
    const out: GhostLine[] = [];
    for (const csc of crossSheetConnectors) {
      let liveBlockId: string | null = null;
      let otherSheetIndex: number | null = null;
      if (csc.fromSheetIndex === activeSheetIndex) {
        liveBlockId = csc.fromBlockId;
        otherSheetIndex = csc.toSheetIndex;
      } else if (csc.toSheetIndex === activeSheetIndex) {
        liveBlockId = csc.toBlockId;
        otherSheetIndex = csc.fromSheetIndex;
      }
      if (!liveBlockId || otherSheetIndex === null) continue;
      const block = blockById.get(liveBlockId);
      if (!block) continue;
      const w = blockDimensions?.[block.id]?.width ?? 200;
      const h = blockDimensions?.[block.id]?.height ?? 80;
      const bx = toScreenX(block.x) * scale;
      const by = toScreenY(block.y) * scale;
      // Map the other sheet to a horizontal position. With N pills
      // evenly distributed across minimapWidth, pill-i centre is at
      // (i + 0.5) / N * width.
      const px = ((otherSheetIndex + 0.5) / sheetCount) * width;
      const py = 0;
      out.push({ cscId: csc.id, bx, by, px, py, toSheet: otherSheetIndex });
      // Suppress unused-var warnings for w/h — kept for parity with the
      // dim-aware bounds calc that other layers use.
      void w;
      void h;
    }
    return out;
  }, [
    blocks,
    blockDimensions,
    crossSheetConnectors,
    activeSheetIndex,
    sheetCount,
    scale,
    toScreenX,
    toScreenY,
    width,
  ]);

  if (lines.length === 0) return null;

  return (
    <svg
      className="absolute inset-0 pointer-events-none"
      width={width}
      height={height}
      data-fw-minimap-csc-ghosts="true"
    >
      {lines.map((l) => (
        <g key={l.cscId} data-fw-minimap-csc-ghost={l.cscId}>
          <line
            x1={l.bx}
            y1={l.by}
            x2={l.px}
            y2={l.py}
            stroke="hsl(var(--primary))"
            strokeWidth={0.75}
            strokeDasharray="2 3"
            opacity={0.45}
          />
          <circle cx={l.px} cy={l.py + 1} r={1.5} fill="hsl(var(--primary))" opacity={0.7} />
        </g>
      ))}
    </svg>
  );
}
